/**
 * Employee Contracts Tab — Arbeitsverträge & Ergänzungen
 *
 * Strukturierte Einträge (Typ, Bezeichnung, Gültig ab/bis, Dokument, Notiz),
 * Upload analog zu „Weiterbildungen & Zertifikate". Zugriff nur für Nutzer mit
 * Zugriff auf sensible Daten (wie Bank/Compliance) — Gating erfolgt im
 * EmployeeForm über den Tab.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { FileSignature, Plus, Trash2, ExternalLink, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateDE } from './helpers';
import { ExpiryBadge } from '../profile/profileHelpers';
import { openAuthFile } from '../../lib/openAuthFile';
import { FileDropzone } from '../../components/ui/file-dropzone';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import { signaturesAPI } from '../../lib/api';
import { TwoPartySignatureDialog } from './TwoPartySignatureDialog';

export const CONTRACT_TYPE_LABELS = {
  arbeitsvertrag: 'Arbeitsvertrag',
  aenderung: 'Änderung',
  ergaenzung: 'Ergänzung',
  aufhebung: 'Aufhebungsvertrag',
};

const extractDocId = (url) => {
  if (!url) return null;
  const m = url.match(/\/documents\/([^/]+)\/download/);
  return m ? m[1] : null;
};

const SignatureStatusBadge = ({ sig, onClick }) => {
  if (!sig) return null;
  const map = {
    done: { cls: 'border-green-500 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/30', Icon: CheckCircle2 },
    pending: { cls: 'border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30', Icon: Clock },
    cancelled: { cls: 'border-muted-foreground/40 text-muted-foreground', Icon: XCircle },
  };
  const { cls, Icon } = map[sig.tone] || map.pending;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Signatur-Status öffnen"
      data-testid={`contract-sig-status-${sig.docId}`}
    >
      <Badge variant="outline" className={`text-xs gap-1 cursor-pointer transition-colors hover:brightness-95 ${cls}`}>
        <Icon className="h-3 w-3" />
        {sig.label}
      </Badge>
    </button>
  );
};

const ContractsTab = ({
  formData,
  employeeId,
  newContract,
  setNewContract,
  addContract,
  removeContract,
  handleContractFileUpload,
  uploadingContract,
}) => {
  const [sigMap, setSigMap] = useState({});
  const [sigDialog, setSigDialog] = useState({ open: false, wf: null, doc: null });

  const loadWorkflows = React.useCallback(() => {
    if (!employeeId) return;
    signaturesAPI
      .listWorkflows({ employee_id: employeeId })
      .then((res) => {
        const map = {};
        (res.data || []).forEach((wf) => {
          if (!wf.document_id || map[wf.document_id]) return; // Liste ist nach created_at desc -> neuester gewinnt
          let label;
          let tone;
          if (wf.status === 'completed') {
            label = 'Vollständig signiert';
            tone = 'done';
          } else if (wf.status === 'cancelled') {
            label = 'Signatur abgebrochen';
            tone = 'cancelled';
          } else {
            const p = wf.parties?.[wf.current_index];
            label = p ? `Warte auf ${p.role_label}` : 'Signatur läuft';
            tone = 'pending';
          }
          map[wf.document_id] = { label, tone, docId: wf.document_id, wf };
        });
        setSigMap(map);
      })
      .catch(() => setSigMap({}));
  }, [employeeId]);

  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const openSigDialog = (c) => {
    const docId = extractDocId(c.document_url);
    const entry = sigMap[docId];
    if (!entry) return;
    setSigDialog({ open: true, wf: entry.wf, doc: { id: docId, title: c.title } });
  };

  return (
    <>
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <FileSignature className="h-5 w-5 text-primary" />
          Arbeitsverträge & Ergänzungen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Vorhandene Verträge */}
        {formData.contracts?.length > 0 && (
          <div className="space-y-3">
            <Label>Hinterlegte Verträge</Label>
            {formData.contracts.map((c) => (
              <div
                key={c.id}
                className="flex items-start justify-between p-4 rounded-lg bg-secondary/30"
                data-testid={`contract-${c.id}`}
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">
                      {CONTRACT_TYPE_LABELS[c.contract_type] || 'Vertrag'}
                    </Badge>
                    <p className="font-medium">{c.title}</p>
                    <SignatureStatusBadge
                      sig={sigMap[extractDocId(c.document_url)]}
                      onClick={() => openSigDialog(c)}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    {c.valid_from && <span>Gültig ab: {formatDateDE(c.valid_from)}</span>}
                    {c.valid_until
                      ? <ExpiryBadge expiry={c.valid_until} prefix="Befristet bis" />
                      : <span>Unbefristet</span>}
                    {c.valid_until && c.reminder_value && (
                      <Badge variant="outline" className="text-xs">
                        Erinnerung: {c.reminder_value} {c.reminder_unit === 'months' ? 'Monate' : 'Tage'} vorher
                      </Badge>
                    )}
                  </div>
                  {c.notes && <p className="text-sm text-muted-foreground italic">„{c.notes}"</p>}
                  {c.document_url ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          openAuthFile(c.document_url, { filename: c.document_filename })
                            .catch((err) => toast.error(`Anzeigen fehlgeschlagen: ${err.message || err}`))
                        }
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        data-testid={`open-contract-doc-${c.id}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                        {c.document_filename || 'Dokument anzeigen'}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          openAuthFile(c.document_url, { filename: c.document_filename || 'vertrag.pdf', forceDownload: true })
                            .catch((err) => toast.error(`Download fehlgeschlagen: ${err.message || err}`))
                        }
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline"
                        data-testid={`download-contract-doc-${c.id}`}
                      >
                        Herunterladen
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">Kein Dokument hinterlegt</span>
                  )}
                </div>
                <ConfirmDeleteDialog
                  trigger={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      data-testid={`remove-contract-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  }
                  title="Vertrag löschen?"
                  description={`Möchten Sie „${c.title}“ wirklich entfernen? ${c.document_url ? 'Das hinterlegte Dokument wird nicht mehr verlinkt sein.' : ''}`}
                  onConfirm={() => removeContract(c.id)}
                  testIdPrefix={`remove-contract-${c.id}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Neuen Vertrag hinzufügen */}
        <div className="space-y-4 p-4 rounded-lg border border-dashed border-border">
          <Label className="text-base font-medium">Vertrag / Ergänzung hinzufügen</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contract_type">Typ *</Label>
              <Select
                value={newContract.contract_type}
                onValueChange={(v) => setNewContract((prev) => ({ ...prev, contract_type: v }))}
              >
                <SelectTrigger id="contract_type" data-testid="contract-type-select">
                  <SelectValue placeholder="Typ wählen" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CONTRACT_TYPE_LABELS).map(([val, label]) => (
                    <SelectItem key={val} value={val}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contract_title">Bezeichnung *</Label>
              <Input
                id="contract_title"
                value={newContract.title || ''}
                onChange={(e) => setNewContract((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="z.B. Arbeitsvertrag unbefristet"
                data-testid="contract-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contract_from">Gültig ab</Label>
              <DatePickerDE
                value={newContract.valid_from || ''}
                onChange={(v) => setNewContract((prev) => ({ ...prev, valid_from: v }))}
                placeholder="TT.MM.JJJJ"
                testId="contract-valid-from"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contract_until">Befristet bis (optional)</Label>
              <DatePickerDE
                value={newContract.valid_until || ''}
                onChange={(v) => setNewContract((prev) => ({ ...prev, valid_until: v }))}
                placeholder="Ablaufdatum"
              />
            </div>
            {newContract.valid_until && (
              <div className="space-y-2 md:col-span-2">
                <Label>Ablauf-Erinnerung (optional, sonst Firmen-Standard)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={newContract.reminder_value ?? ''}
                    onChange={(e) => setNewContract((prev) => ({ ...prev, reminder_value: e.target.value }))}
                    placeholder="Firmen-Standard"
                    className="max-w-[160px]"
                    data-testid="contract-reminder-value"
                  />
                  <Select
                    value={newContract.reminder_unit || 'days'}
                    onValueChange={(v) => setNewContract((prev) => ({ ...prev, reminder_unit: v }))}
                  >
                    <SelectTrigger className="w-[140px]" data-testid="contract-reminder-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">Tage vorher</SelectItem>
                      <SelectItem value="months">Monate vorher</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">
                  Leer lassen = firmenweite Vorlaufzeit verwenden. Berechtigte werden vor Ablauf benachrichtigt.
                </p>
              </div>
            )}
            <div className="space-y-2 md:col-span-2">
              <Label>Dokument hochladen</Label>
              <FileDropzone
                accept=".pdf,.jpg,.jpeg,.png"
                onUpload={handleContractFileUpload}
                uploading={uploadingContract}
                filename={newContract.document_filename}
                testId="contract-file-dropzone"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="contract_notes">Notiz (optional)</Label>
              <Textarea
                id="contract_notes"
                value={newContract.notes || ''}
                onChange={(e) => setNewContract((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Optionale Anmerkung..."
                rows={2}
              />
            </div>
          </div>
          <Button
            type="button"
            onClick={addContract}
            disabled={!newContract.title.trim()}
            data-testid="add-contract-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Vertrag hinzufügen
          </Button>
        </div>
      </CardContent>
    </Card>
    <TwoPartySignatureDialog
      open={sigDialog.open}
      onOpenChange={(v) => setSigDialog((s) => ({ ...s, open: v }))}
      doc={sigDialog.doc}
      existingWorkflow={sigDialog.wf}
      onChanged={loadWorkflows}
    />
    </>
  );
};

export default ContractsTab;
